/**
 * ToastHost.js — single-toast mount point + useToast hook.
 *
 * Contract:
 *   <ToastHost /> — renders at most one toast at a time. Mounted once near
 *                   the top of the app tree (inside AppLayout's root div).
 *
 *   useToast()  → { showToast(options), dismissToast() }
 *     showToast(options) replaces any currently-visible toast (single-toast
 *     replacement semantics — D.4 auto-capture toasts must not stack when
 *     several fire rapidly).
 *
 *     Options match Toast.js props:
 *       { message, actionLabel?, onAction?, variant?, durationMs?, onExpire? }
 *
 *     Returns a token (increment-only id) so callers can optionally check
 *     whether their toast is still the active one before calling dismissToast.
 *
 *     dismissToast() clears the current toast.
 *
 * Data flow:
 *
 *     Component A                ToastProvider            <ToastHost />
 *         │                            │                        │
 *         │ useToast().showToast({...})│                        │
 *         ├───────────────────────────▶│ setCurrent({...})      │
 *         │                            ├───────────────────────▶│
 *         │                            │                        │ renders <Toast .../>
 *         │                            │                        │
 *         │                            │ (on expire / action)   │
 *         │                            │◀───────────────────────┤ onExpire fires
 *         │                            │ setCurrent(null)       │
 *         │                            │                        │ (unmounts)
 */

import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import Toast from './Toast';


const ToastCtx = createContext(null);


export function ToastProvider({ children }) {
  // current: { token, options } | null
  const [current, setCurrent] = useState(null);
  const tokenRef = useRef(0);

  const showToast = useCallback((options) => {
    tokenRef.current += 1;
    const token = tokenRef.current;
    setCurrent({ token, options });
    return token;
  }, []);

  const dismissToast = useCallback(() => {
    setCurrent(null);
  }, []);

  return (
    <ToastCtx.Provider value={{ showToast, dismissToast, current }}>
      {children}
    </ToastCtx.Provider>
  );
}


export function useToast() {
  const ctx = useContext(ToastCtx);
  if (!ctx) {
    throw new Error('useToast must be used within a <ToastProvider>');
  }
  return { showToast: ctx.showToast, dismissToast: ctx.dismissToast };
}


/**
 * ToastHost — renders the currently-visible toast, if any. Must be a
 * descendant of <ToastProvider>. Mounted once per app.
 */
export default function ToastHost() {
  const ctx = useContext(ToastCtx);
  if (!ctx || !ctx.current) return null;

  const { token, options } = ctx.current;
  const { onAction, onExpire, ...rest } = options;

  // Wrap handlers so any action / expiry clears state — callers can't forget
  // to dismiss. If a newer toast has replaced this one, `token` no longer
  // matches; we guard so a late-firing expire from the OLD toast doesn't wipe
  // the NEW one.
  const wrappedAction = onAction
    ? () => {
        try {
          onAction();
        } finally {
          if (ctx.current?.token === token) ctx.dismissToast();
        }
      }
    : null;

  const wrappedExpire = () => {
    try {
      if (onExpire) onExpire();
    } finally {
      if (ctx.current?.token === token) ctx.dismissToast();
    }
  };

  return (
    <Toast
      key={token}
      {...rest}
      onAction={wrappedAction}
      onExpire={wrappedExpire}
    />
  );
}
